import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapMeta } from '../../types/map';
import { mapsApi } from '../../services/api';
import { useEditor } from '../../contexts/EditorContext';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import CreateMapModal from '../CreateMapModal';
import DeleteMapModal from '../DeleteMapModal';
import GeneralSettingsTab from '../MapSettingsPanel/GeneralSettingsTab';
import './MapBrowserModal.css';

interface MapBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FolderNode {
  name: string;
  path: string;
  isOpen: boolean;
  children: FolderNode[];
}

interface DragState {
  type: 'map' | 'folder' | null;
  id: string | null;
  sourcePath: string | null;
}

interface DropTarget {
  type: 'folder' | 'root' | null;
  path: string | null;
}

// Local storage key for open folders state
const OPEN_FOLDERS_KEY = 'map-browser-open-folders';

type SettingsTab = 'info' | 'general';

const SETTINGS_TABS: { id: SettingsTab; label: string; icon: string }[] = [
  { id: 'info', label: 'Info', icon: 'fa-info-circle' },
  { id: 'general', label: 'General', icon: 'fa-sliders-h' }
];

/**
 * Full-screen modal for browsing maps with folder tree structure
 * Same design pattern as AssetTab folders
 */
function MapBrowserModal({ isOpen, onClose }: MapBrowserModalProps): JSX.Element | null {
  const { maps: contextMaps, selectedMapId, isLoadingMaps, selectMap, refreshMaps, updateSelectedMap } = useEditor();
  const { user } = useAuth();
  // Local state for maps to allow immediate UI updates
  const [maps, setMaps] = useState<MapMeta[]>([]);
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [previewMapId, setPreviewMapId] = useState<string | null>(null);
  const [dragState, setDragState] = useState<DragState>({ type: null, id: null, sourcePath: null });
  const [dropTarget, setDropTarget] = useState<DropTarget>({ type: null, path: null });
  const [editingFolderPath, setEditingFolderPath] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState<string>('');
  const [activeSettingsTab, setActiveSettingsTab] = useState<SettingsTab>('info');
  const folderInputRef = useRef<HTMLInputElement>(null);

  const canEdit = user && hasPermission(user.permissions, PERMISSIONS.MAP_EDIT);
  const canDelete = user && hasPermission(user.permissions, PERMISSIONS.MAP_DELETE);
  const canCreate = user && hasPermission(user.permissions, PERMISSIONS.MAP_CREATE);

  // Sync local maps state with context
  useEffect(() => {
    setMaps(contextMaps);
  }, [contextMaps]);

  // Load open folders state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(OPEN_FOLDERS_KEY);
    if (stored) {
      try {
        setOpenFolders(new Set(JSON.parse(stored)));
      } catch {
        setOpenFolders(new Set());
      }
    }
  }, []);

  // Save open folders to localStorage
  const saveOpenFolders = useCallback((newOpenFolders: Set<string>) => {
    setOpenFolders(newOpenFolders);
    localStorage.setItem(OPEN_FOLDERS_KEY, JSON.stringify([...newOpenFolders]));
  }, []);

  // Build folder tree from map paths
  const folderTree = useMemo((): FolderNode[] => {
    const allPaths = new Set<string>();

    // Collect all folder paths from maps
    maps.forEach(map => {
      if (map.folder) {
        const parts = map.folder.split('/');
        let currentPath = '';
        parts.forEach(part => {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          allPaths.add(currentPath);
        });
      }
    });

    // Also include paths from openFolders (for empty folders)
    openFolders.forEach(path => {
      const parts = path.split('/');
      let currentPath = '';
      parts.forEach(part => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        allPaths.add(currentPath);
      });
    });

    // Build tree structure
    const buildTree = (parentPath: string | null): FolderNode[] => {
      const nodes: FolderNode[] = [];
      const pathsAtLevel = new Set<string>();

      allPaths.forEach(path => {
        const parts = path.split('/');
        if (parentPath === null) {
          if (parts.length >= 1) {
            pathsAtLevel.add(parts[0]);
          }
        } else {
          const parentParts = parentPath.split('/');
          if (parts.length === parentParts.length + 1 && path.startsWith(parentPath + '/')) {
            pathsAtLevel.add(path);
          }
        }
      });

      pathsAtLevel.forEach(path => {
        const fullPath = parentPath === null ? path : path;
        const name = path.split('/').pop() || path;
        nodes.push({
          name,
          path: fullPath,
          isOpen: openFolders.has(fullPath),
          children: buildTree(fullPath)
        });
      });

      return nodes.sort((a, b) => a.name.localeCompare(b.name));
    };

    return buildTree(null);
  }, [maps, openFolders]);

  // Get previewed map
  const previewMap = useMemo(() => {
    if (!previewMapId) return null;
    return maps.find(m => m.id === previewMapId) || null;
  }, [maps, previewMapId]);

  // Filter maps by search query
  const filteredMaps = useMemo(() => {
    if (!searchQuery.trim()) return maps;
    const query = searchQuery.toLowerCase();
    return maps.filter(
      (map) =>
        map.label.toLowerCase().includes(query) ||
        map.id.toLowerCase().includes(query) ||
        (map.folder && map.folder.toLowerCase().includes(query))
    );
  }, [maps, searchQuery]);

  // Root level maps (no folder)
  const rootMaps = useMemo(() => maps.filter(m => !m.folder), [maps]);

  // Refresh maps when modal opens
  useEffect(() => {
    if (isOpen) {
      refreshMaps();
      setPreviewMapId(selectedMapId);
    }
  }, [isOpen, refreshMaps, selectedMapId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (showCreateModal || showDeleteModal) return;
        if (editingFolderPath) {
          setEditingFolderPath(null);
          setEditingFolderName('');
        } else if (isOpen) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, editingFolderPath, showCreateModal, showDeleteModal, onClose]);

  // Toggle folder open/closed
  const toggleFolder = (folderPath: string): void => {
    const newOpenFolders = new Set(openFolders);
    if (newOpenFolders.has(folderPath)) {
      newOpenFolders.delete(folderPath);
    } else {
      newOpenFolders.add(folderPath);
    }
    saveOpenFolders(newOpenFolders);
  };

  // Update map folder via API and local state
  const updateMapFolder = async (mapId: string, folderPath: string | null): Promise<void> => {
    try {
      // Pass null explicitly to remove folder, or the path to set it
      await mapsApi.updateSettings(mapId, { folder: folderPath === null ? null : folderPath });
      // Update local state immediately for responsive UI
      setMaps(prev => prev.map(m => m.id === mapId ? { ...m, folder: folderPath || undefined } : m));
    } catch (err) {
      console.error('Failed to update map folder:', err);
    }
  };

  // Generate unique folder name
  const generateUniqueFolderName = (parentPath: string | null): string => {
    const existingNames = new Set<string>();
    maps.forEach(map => {
      if (map.folder) {
        if (parentPath === null) {
          const firstSegment = map.folder.split('/')[0];
          existingNames.add(firstSegment);
        } else if (map.folder.startsWith(parentPath + '/')) {
          const remaining = map.folder.slice(parentPath.length + 1);
          const nextSegment = remaining.split('/')[0];
          if (nextSegment) existingNames.add(nextSegment);
        }
      }
    });

    // Also check openFolders for empty folders
    openFolders.forEach(path => {
      if (parentPath === null) {
        const firstSegment = path.split('/')[0];
        existingNames.add(firstSegment);
      } else if (path.startsWith(parentPath + '/')) {
        const remaining = path.slice(parentPath.length + 1);
        const nextSegment = remaining.split('/')[0];
        if (nextSegment) existingNames.add(nextSegment);
      }
    });

    let counter = 1;
    let name = 'New Folder';
    while (existingNames.has(name)) {
      counter++;
      name = `New Folder ${counter}`;
    }
    return name;
  };

  // Create new folder
  const handleCreateFolder = (): void => {
    const folderName = generateUniqueFolderName(null);
    const newPath = folderName;

    // Add to open folders - folder will persist when a map is moved into it
    const newOpenFolders = new Set(openFolders);
    newOpenFolders.add(newPath);
    saveOpenFolders(newOpenFolders);

    // Start editing the folder name
    setEditingFolderPath(newPath);
    setEditingFolderName(folderName);
    setTimeout(() => folderInputRef.current?.select(), 0);
  };

  // Create subfolder
  const handleCreateSubfolder = (parentPath: string): void => {
    const folderName = generateUniqueFolderName(parentPath);
    const newPath = `${parentPath}/${folderName}`;

    const newOpenFolders = new Set(openFolders);
    newOpenFolders.add(parentPath); // Ensure parent is open
    newOpenFolders.add(newPath);
    saveOpenFolders(newOpenFolders);

    setEditingFolderPath(newPath);
    setEditingFolderName(folderName);
    setTimeout(() => folderInputRef.current?.select(), 0);
  };

  const handleFolderNameChange = (value: string): void => {
    setEditingFolderName(value.replace(/\//g, ''));
  };

  const handleFolderNameSubmit = async (): Promise<void> => {
    if (!editingFolderPath) return;
    const trimmedName = editingFolderName.trim();
    if (!trimmedName) {
      setEditingFolderPath(null);
      setEditingFolderName('');
      return;
    }

    const oldPath = editingFolderPath;
    const pathParts = oldPath.split('/');
    pathParts[pathParts.length - 1] = trimmedName;
    const newPath = pathParts.join('/');

    if (oldPath !== newPath) {
      // Update all maps that have this folder path or are in subfolders
      const mapsToUpdate = maps.filter(m =>
        m.folder === oldPath || (m.folder && m.folder.startsWith(oldPath + '/'))
      );

      for (const map of mapsToUpdate) {
        const updatedFolder = map.folder === oldPath
          ? newPath
          : newPath + map.folder!.slice(oldPath.length);
        await mapsApi.updateSettings(map.id, { folder: updatedFolder });
      }

      // Update local state
      setMaps(prev => prev.map(m => {
        if (m.folder === oldPath) {
          return { ...m, folder: newPath };
        } else if (m.folder && m.folder.startsWith(oldPath + '/')) {
          return { ...m, folder: newPath + m.folder.slice(oldPath.length) };
        }
        return m;
      }));

      // Update open folders
      const newOpenFolders = new Set<string>();
      openFolders.forEach(path => {
        if (path === oldPath) {
          newOpenFolders.add(newPath);
        } else if (path.startsWith(oldPath + '/')) {
          newOpenFolders.add(newPath + path.slice(oldPath.length));
        } else {
          newOpenFolders.add(path);
        }
      });
      saveOpenFolders(newOpenFolders);
    }

    setEditingFolderPath(null);
    setEditingFolderName('');
  };

  const handleFolderNameKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFolderNameSubmit();
    } else if (e.key === 'Escape') {
      setEditingFolderPath(null);
      setEditingFolderName('');
    }
  };

  // Delete folder (move maps to root)
  const deleteFolder = async (folderPath: string): Promise<void> => {
    const mapsToUpdate = maps.filter(m =>
      m.folder === folderPath || (m.folder && m.folder.startsWith(folderPath + '/'))
    );

    for (const map of mapsToUpdate) {
      await mapsApi.updateSettings(map.id, { folder: undefined });
    }

    // Update local state
    setMaps(prev => prev.map(m => {
      if (m.folder === folderPath || (m.folder && m.folder.startsWith(folderPath + '/'))) {
        return { ...m, folder: undefined };
      }
      return m;
    }));

    // Remove from open folders
    const newOpenFolders = new Set<string>();
    openFolders.forEach(path => {
      if (path !== folderPath && !path.startsWith(folderPath + '/')) {
        newOpenFolders.add(path);
      }
    });
    saveOpenFolders(newOpenFolders);
  };

  // Drag and drop handlers
  const handleDragStart = (type: 'map' | 'folder', id: string, sourcePath: string | null): void => {
    setDragState({ type, id, sourcePath });
  };

  const handleDragEnd = (): void => {
    setDragState({ type: null, id: null, sourcePath: null });
    setDropTarget({ type: null, path: null });
  };

  const handleDragOver = (e: React.DragEvent, targetType: 'folder' | 'root', targetPath: string | null): void => {
    e.preventDefault();
    e.stopPropagation();

    // Don't allow dropping folder into itself or its descendants
    if (dragState.type === 'folder' && dragState.id) {
      if (targetType === 'folder' && targetPath) {
        if (targetPath === dragState.id || targetPath.startsWith(dragState.id + '/')) {
          return;
        }
      }
    }

    setDropTarget({ type: targetType, path: targetPath });
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDropTarget({ type: null, path: null });
    }
  };

  const handleDrop = async (e: React.DragEvent, targetType: 'folder' | 'root', targetPath: string | null): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragState.type || !dragState.id) return;

    // Don't drop on same location
    if (dragState.type === 'map') {
      const map = maps.find(m => m.id === dragState.id);
      const mapFolder = map?.folder || null;
      if (mapFolder === targetPath) {
        handleDragEnd();
        return;
      }
      await updateMapFolder(dragState.id, targetPath);
    } else if (dragState.type === 'folder') {
      const sourceFolderPath = dragState.id;

      // Don't drop folder into itself or descendants
      if (targetType === 'folder' && targetPath) {
        if (targetPath === sourceFolderPath || targetPath.startsWith(sourceFolderPath + '/')) {
          handleDragEnd();
          return;
        }
      }

      // Calculate new path for the folder
      const folderName = sourceFolderPath.split('/').pop() || sourceFolderPath;
      const newFolderPath = targetPath ? `${targetPath}/${folderName}` : folderName;

      // Don't drop on same parent
      const sourceParent = sourceFolderPath.includes('/')
        ? sourceFolderPath.substring(0, sourceFolderPath.lastIndexOf('/'))
        : null;
      if (sourceParent === targetPath) {
        handleDragEnd();
        return;
      }

      // Update all maps in the source folder
      const mapsToUpdate = maps.filter(m =>
        m.folder === sourceFolderPath || (m.folder && m.folder.startsWith(sourceFolderPath + '/'))
      );

      for (const map of mapsToUpdate) {
        const newMapFolder = map.folder === sourceFolderPath
          ? newFolderPath
          : newFolderPath + map.folder!.slice(sourceFolderPath.length);
        await mapsApi.updateSettings(map.id, { folder: newMapFolder });
      }

      // Update local state
      setMaps(prev => prev.map(m => {
        if (m.folder === sourceFolderPath) {
          return { ...m, folder: newFolderPath };
        } else if (m.folder && m.folder.startsWith(sourceFolderPath + '/')) {
          return { ...m, folder: newFolderPath + m.folder.slice(sourceFolderPath.length) };
        }
        return m;
      }));

      // Update open folders
      const newOpenFolders = new Set<string>();
      openFolders.forEach(path => {
        if (path === sourceFolderPath) {
          newOpenFolders.add(newFolderPath);
        } else if (path.startsWith(sourceFolderPath + '/')) {
          newOpenFolders.add(newFolderPath + path.slice(sourceFolderPath.length));
        } else {
          newOpenFolders.add(path);
        }
      });
      if (targetPath) {
        newOpenFolders.add(targetPath); // Open target folder
      }
      saveOpenFolders(newOpenFolders);
    }

    handleDragEnd();
  };

  // Get folder stats
  const getFolderStats = useCallback((folderPath: string): { mapCount: number; subfolderCount: number } => {
    const mapsInFolder = maps.filter(m =>
      m.folder === folderPath || (m.folder && m.folder.startsWith(folderPath + '/'))
    );

    const directSubfolders = new Set<string>();
    maps.forEach(m => {
      if (m.folder && m.folder.startsWith(folderPath + '/')) {
        const remaining = m.folder.slice(folderPath.length + 1);
        const nextSegment = remaining.split('/')[0];
        if (nextSegment) {
          directSubfolders.add(nextSegment);
        }
      }
    });

    return {
      mapCount: mapsInFolder.length,
      subfolderCount: directSubfolders.size
    };
  }, [maps]);

  // Map click handlers
  const handleMapClick = useCallback((map: MapMeta): void => {
    setPreviewMapId(map.id);
  }, []);

  const handleMapDoubleClick = useCallback((map: MapMeta): void => {
    selectMap(map.id);
    onClose();
  }, [selectMap, onClose]);

  const handleOpenInEditor = useCallback((): void => {
    if (previewMapId) {
      selectMap(previewMapId);
      onClose();
    }
  }, [previewMapId, selectMap, onClose]);

  // Render folder recursively
  const renderFolder = (folder: FolderNode, depth: number = 0): JSX.Element => {
    const folderMaps = maps.filter(m => m.folder === folder.path);
    const stats = getFolderStats(folder.path);
    const isDropTargetFolder = dropTarget.type === 'folder' && dropTarget.path === folder.path;
    const isDragging = dragState.type === 'folder' && dragState.id === folder.path;
    const isEditing = editingFolderPath === folder.path;

    return (
      <div key={folder.path} className="map-folder" style={{ marginLeft: depth * 16 }}>
        <div
          className={`map-folder-header ${isDropTargetFolder ? 'map-folder-header--drop-target' : ''} ${isDragging ? 'map-folder-header--dragging' : ''}`}
          draggable={!isEditing && canEdit}
          onDragStart={(e) => {
            if (isEditing || !canEdit) return;
            e.stopPropagation();
            handleDragStart('folder', folder.path, folder.path.includes('/') ? folder.path.substring(0, folder.path.lastIndexOf('/')) : null);
          }}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, 'folder', folder.path)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'folder', folder.path)}
        >
          <button
            className="map-folder-toggle"
            onClick={() => toggleFolder(folder.path)}
          >
            <i className={`fas fa-chevron-${folder.isOpen ? 'down' : 'right'}`}></i>
          </button>
          <i className={`fas fa-folder${folder.isOpen ? '-open' : ''} map-folder-icon`}></i>
          {isEditing ? (
            <input
              ref={folderInputRef}
              className="map-folder-name-input"
              type="text"
              value={editingFolderName}
              onChange={(e) => handleFolderNameChange(e.target.value)}
              onBlur={handleFolderNameSubmit}
              onKeyDown={handleFolderNameKeyDown}
              autoFocus
            />
          ) : (
            <span
              className="map-folder-name"
              onDoubleClick={() => {
                if (!canEdit) return;
                setEditingFolderPath(folder.path);
                setEditingFolderName(folder.name);
              }}
            >
              {folder.name}
            </span>
          )}
          <span className="map-folder-stats">
            {stats.mapCount > 0 && <span className="map-folder-stat">{stats.mapCount}</span>}
            {stats.subfolderCount > 0 && <span className="map-folder-stat"><i className="fas fa-folder"></i> {stats.subfolderCount}</span>}
          </span>
          {canEdit && (
            <button
              className="map-folder-add-subfolder"
              onClick={(e) => {
                e.stopPropagation();
                handleCreateSubfolder(folder.path);
              }}
              title="Create subfolder"
            >
              <i className="fas fa-folder-plus"></i>
            </button>
          )}
          {canEdit && (
            <button
              className="map-folder-delete"
              onClick={(e) => {
                e.stopPropagation();
                deleteFolder(folder.path);
              }}
              title="Delete folder"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
        {folder.isOpen && (
          <div className="map-folder-content">
            {folder.children.map(child => renderFolder(child, depth + 1))}
            {folderMaps.map(map => renderMapItem(map, depth + 1))}
            {folder.children.length === 0 && folderMaps.length === 0 && (
              <div className="map-folder-empty" style={{ marginLeft: (depth + 1) * 16 }}>
                Empty folder
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render map item
  const renderMapItem = (map: MapMeta, depth: number = 0): JSX.Element => {
    const isDragging = dragState.type === 'map' && dragState.id === map.id;
    const isSelected = previewMapId === map.id;

    return (
      <div
        key={map.id}
        className={`map-item ${isDragging ? 'map-item--dragging' : ''} ${isSelected ? 'map-item--selected' : ''}`}
        style={{ marginLeft: depth * 16 }}
        draggable={canEdit}
        onDragStart={(e) => {
          if (!canEdit) return;
          e.stopPropagation();
          handleDragStart('map', map.id, map.folder || null);
        }}
        onDragEnd={handleDragEnd}
        onClick={() => handleMapClick(map)}
        onDoubleClick={() => handleMapDoubleClick(map)}
      >
        <i className="fas fa-map map-item-icon"></i>
        <div className="map-item-info">
          <span className="map-item-label">{map.label}</span>
        </div>
        <div className="map-item-id">
          <span>{map.id}</span>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const isRootDropTarget = dropTarget.type === 'root';

  const modalContent = (
    <div className="map-browser-backdrop" onClick={onClose}>
      <div className="map-browser" onClick={(e) => e.stopPropagation()}>
        <div className="map-browser-header">
          <span className="map-browser-title">
            <i className="fas fa-map"></i>
            Maps
          </span>
          <div className="map-browser-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search maps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                className="map-browser-search-clear"
                onClick={() => setSearchQuery('')}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <button className="map-browser-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="map-browser-content">
          <div className="map-browser-sidebar">
            <div
              className={`map-browser-list ${isRootDropTarget ? 'map-browser-list--drop-target' : ''}`}
              onDragOver={(e) => handleDragOver(e, 'root', null)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'root', null)}
            >
              {isLoadingMaps ? (
                <div className="map-browser-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading maps...</span>
                </div>
              ) : maps.length === 0 && folderTree.length === 0 ? (
                <div className="map-browser-empty">
                  <i className="fas fa-map"></i>
                  <span>No maps yet</span>
                  <span className="map-browser-empty-hint">Click + to create a new map</span>
                </div>
              ) : searchQuery.trim() ? (
                <div className="map-browser-search-results">
                  {filteredMaps.length === 0 ? (
                    <div className="map-browser-empty">
                      <i className="fas fa-search"></i>
                      <span>No maps found</span>
                    </div>
                  ) : (
                    filteredMaps
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((map) => (
                        <div
                          key={map.id}
                          className={`map-item ${previewMapId === map.id ? 'map-item--selected' : ''}`}
                          onClick={() => handleMapClick(map)}
                          onDoubleClick={() => handleMapDoubleClick(map)}
                        >
                          <i className="fas fa-map map-item-icon"></i>
                          <div className="map-item-info">
                            <span className="map-item-label">{map.label}</span>
                          </div>
                          {map.folder && (
                            <span className="map-item-folder">{map.folder}</span>
                          )}
                          <div className="map-item-id">
                            <span>{map.id}</span>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              ) : (
                <div className="map-browser-list-items">
                  {folderTree.map(folder => renderFolder(folder))}
                  {rootMaps.map(map => renderMapItem(map))}
                </div>
              )}
            </div>

            {/* Floating Action Buttons */}
            {canEdit && !searchQuery.trim() && (
              <div className="map-browser-fab-container">
                <button
                  className="map-browser-fab map-browser-fab-folder"
                  onClick={handleCreateFolder}
                  title="Create new folder"
                >
                  <i className="fas fa-folder-plus"></i>
                </button>
                {canCreate && (
                  <button
                    className="map-browser-fab map-browser-fab-add"
                    onClick={() => setShowCreateModal(true)}
                    title="Create new map"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="map-browser-details">
            {previewMap ? (
              <>
                <div className="map-browser-details-header">
                  <div className="map-browser-details-title">
                    <h3>{previewMap.label}</h3>
                    <span className="map-browser-details-id">{previewMap.id}</span>
                  </div>
                  <div className="map-browser-details-header-actions">
                    <button
                      className="map-browser-details-open"
                      onClick={handleOpenInEditor}
                    >
                      <i className="fas fa-edit"></i>
                      <span>Open in Editor</span>
                    </button>
                    {canDelete && (
                      <button
                        className="map-browser-details-delete"
                        onClick={() => setShowDeleteModal(true)}
                        title="Delete map"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="map-browser-details-tabs">
                    {SETTINGS_TABS.map((tab) => (
                      <button
                        key={tab.id}
                        className={`map-browser-details-tab ${activeSettingsTab === tab.id ? 'map-browser-details-tab--active' : ''}`}
                        onClick={() => setActiveSettingsTab(tab.id)}
                      >
                        <i className={`fas ${tab.icon}`}></i>
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="map-browser-details-content">
                  {activeSettingsTab === 'info' ? (
                    <div className="map-browser-details-info">
                      {previewMap.folder && (
                        <div className="map-browser-details-row">
                          <i className="fas fa-folder"></i>
                          <span>{previewMap.folder}</span>
                        </div>
                      )}
                      <div className="map-browser-details-row">
                        <i className="fas fa-ruler"></i>
                        <span>Scale: {previewMap.scale || 128}px/m</span>
                      </div>
                      <div className="map-browser-details-row">
                        <i className="fas fa-user"></i>
                        <span>Created by: {previewMap.createdBy}</span>
                      </div>
                      <div className="map-browser-details-row">
                        <i className="fas fa-clock"></i>
                        <span>Updated: {new Date(previewMap.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ) : (
                    <GeneralSettingsTab
                      map={previewMap}
                      onMapUpdate={(updatedMap) => {
                        updateSelectedMap(updatedMap);
                        refreshMaps();
                      }}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="map-browser-details-empty">
                <i className="fas fa-mouse-pointer"></i>
                <span>Select a map to view details</span>
                <p>Double-click to open in editor</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateMapModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <DeleteMapModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        map={previewMap}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default MapBrowserModal;
